import React, { useEffect } from 'react';
import { MdClose, MdAccessTime, MdEdit } from 'react-icons/md';

const AssignmentPreview = ({ assignment, submission = null, onClose, onWorkOnAssignment, onOpenComponent, onViewGrade }) => {
  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (dueDate) => {
    if (!dueDate) return { text: 'No due date', className: 'text-gray-600', percentage: 0, isOverdue: false };

    const now = new Date();
    const due = new Date(dueDate);
    const diff = due - now;

    if (diff < 0) {
      const overdueDays = Math.floor(Math.abs(diff) / (1000 * 60 * 60 * 24));
      return {
        text: overdueDays > 0 ? `${overdueDays} days overdue` : 'Overdue',
        className: 'text-red-600 font-semibold',
        percentage: 100,
        isOverdue: true
      };
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const totalTime = 14 * 24 * 60 * 60 * 1000;
    const elapsed = totalTime - diff;
    const percentage = Math.min(100, Math.max(0, (elapsed / totalTime) * 100));

    let text;
    if (days > 7) {
      text = `${days} days remaining`;
      return { text, className: 'text-gray-600', percentage, isOverdue: false };
    } else if (days > 0) {
      text = `${days}d ${hours}h remaining`;
      return { text, className: 'text-yellow-600 font-semibold', percentage, isOverdue: false };
    } else if (hours > 0) {
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      text = `${hours}h ${minutes}m remaining`;
      return { text, className: 'text-orange-600 font-semibold', percentage, isOverdue: false };
    } else {
      const minutes = Math.floor(diff / (1000 * 60));
      text = `${minutes}m remaining`;
      return { text, className: 'text-red-600 font-semibold', percentage, isOverdue: false };
    }
  };

  const hasComponents = assignment.components && assignment.components.length > 0;
  const timeRemaining = getTimeRemaining(assignment.due_date);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-gray-50 shadow-2xl z-50 overflow-hidden flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 m-0 truncate">{assignment.title}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-500">{assignment.total_points} pts</span>
              {hasComponents && (
                <span className="text-sm text-gray-400">{assignment.components.length} components</span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 ml-2"
          >
            <MdClose className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Due Date */}
          {assignment.due_date && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <MdAccessTime className="text-base text-gray-500" />
                <h4 className="text-sm font-semibold text-gray-900 m-0">Due Date</h4>
              </div>
              <p className="text-sm text-gray-700 mb-2 m-0">{formatDate(assignment.due_date)}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      timeRemaining.isOverdue ? 'bg-red-500' : 'bg-blue-600'
                    }`}
                    style={{ width: `${timeRemaining.percentage}%` }}
                  />
                </div>
                <span className={`text-xs whitespace-nowrap ${timeRemaining.className}`}>
                  {timeRemaining.text}
                </span>
              </div>
            </div>
          )}

          {/* Description */}
          {assignment.description && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
              <h4 className="text-sm font-semibold text-gray-900 m-0 mb-2">Description</h4>
              <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {assignment.description}
              </div>
            </div>
          )}

          {/* Components */}
          {hasComponents && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
              <h4 className="text-sm font-semibold text-gray-900 m-0 mb-3">Components</h4>
              <div className="space-y-2">
                {assignment.components.sort((a, b) => a.order - b.order).map((comp, idx) => {
                  const colorClass = comp.type === 'document' ? 'bg-blue-100 text-blue-700' :
                    comp.type === 'erd' ? 'bg-purple-100 text-purple-700' :
                    'bg-green-100 text-green-700';
                  const icon = comp.type === 'document' ? 'DOC' : comp.type === 'erd' ? 'ERD' : 'CODE';
                  return (
                    <div
                      key={comp.component_id || idx}
                      className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      onClick={() => onOpenComponent && onOpenComponent(comp, assignment)}
                    >
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${colorClass}`}>{icon}</span>
                      <span className="text-sm text-gray-800 font-medium flex-1">{comp.title || comp.type}</span>
                      <span className="text-xs text-gray-500">{comp.points} pts</span>
                      <MdEdit className="text-gray-400 text-sm" />
                    </div>
                  );
                })}
                <div className="flex justify-between px-3 py-1.5 text-xs text-gray-500 border-t border-gray-100 mt-1">
                  <span>Total</span>
                  <span>{assignment.total_points} pts</span>
                </div>
              </div>
            </div>
          )}

          {/* Additional Info */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
            <h4 className="text-sm font-semibold text-gray-900 m-0 mb-2">Info</h4>
            <div className="space-y-1.5 text-sm text-gray-600">
              {assignment.lock_after_due_date && (
                <p className="m-0">Locked after due date</p>
              )}
              {assignment.allow_late_submission && assignment.late_due_date && (
                <p className="m-0">
                  Late submissions until {formatDate(assignment.late_due_date)}
                  {assignment.late_penalty_type && (
                    <span className="text-orange-600 font-medium ml-1">(with penalty)</span>
                  )}
                </p>
              )}
              {!assignment.allow_late_submission && (
                <p className="m-0 text-red-600 font-medium">Late submissions not accepted</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-white border-t border-gray-200 px-5 py-4 flex-shrink-0 space-y-2">
          {submission && (submission.status === 'returned' || submission.status === 'graded') && onViewGrade && (
            <button
              onClick={() => onViewGrade(assignment)}
              className="w-full px-4 py-3 bg-green-600 text-white font-semibold text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              View Grade {submission.score != null ? `(${submission.score}%)` : ''}
            </button>
          )}
          <button
            onClick={() => onWorkOnAssignment(assignment)}
            className={`w-full px-4 py-3 font-semibold text-sm rounded-lg transition-colors ${
              submission && (submission.status === 'returned' || submission.status === 'graded')
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {submission?.status === 'submitted' ? 'View Submission' : 'Work on Assignment'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default AssignmentPreview;
